import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  CircularProgress,
  Typography,
  ToggleButton,
  Skeleton,
  TextField,
  Autocomplete,
  Box,
} from "@mui/material";
import { IoSearch } from "react-icons/io5";
import _ from "lodash";

const BookSearch = ({
  selectedBooks,
  addSelectedBook,
  setLoading,
  loading,
  isDisabled,
}) => {
  const searchInputRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchFilter, setSearchFilter] = useState("b");
  const [cachedSearches, setCachedSearches] = useState({});
  const [bookSearchResults, setBookSearchResults] = useState([]);

  // Focus input on initial render
  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  const debouncedSearchBooks = useMemo(() => {
    return _.debounce(async (text, filter) => {
      if (!text) {
        setBookSearchResults([]);
        return;
      }

      const cacheKey = `${filter}:${text}`;
      if (cachedSearches[cacheKey]) {
        setBookSearchResults(cachedSearches[cacheKey]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // const searchResults = await searchBooksApi(text, filter);

        const response = await fetch(
          `/api/books?query=${encodeURIComponent(
            text
          )}&filteredBy=${encodeURIComponent(filter)}`
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch search results: ${response.statusText}`
          );
        }

        const searchResults = await response.json();

        setCachedSearches((prev) => ({ ...prev, [cacheKey]: searchResults }));
        setBookSearchResults(searchResults);
      } catch (error) {
        console.error("Error in searchBooks:", error);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, [cachedSearches, setLoading]);

  // Trigger search when text changes
  useEffect(() => {
    debouncedSearchBooks(searchText, searchFilter);
    // Cleanup to cancel any pending debounce calls on unmount or changes
    return () => debouncedSearchBooks.cancel();
  }, [searchText, searchFilter, debouncedSearchBooks]);

  const handleSearchTextChange = (event) => {
    setSearchText(event?.target?.value || "");
  };

  const addBook = (book) => {
    setSearchText("");
    addSelectedBook(book);
    setBookSearchResults([]);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        width: "100%",
      }}
    >
      <Autocomplete
        freeSolo
        style={{ flexGrow: 1, borderRadius: "50px", width: "50%" }}
        open={open}
        options={bookSearchResults}
        inputValue={searchText}
        value={null}
        disabled={isDisabled}
        filterOptions={(x) => x}
        loading={loading}
        getOptionDisabled={(option) =>
          selectedBooks.some((book) => book.id === option.id)
        }
        getOptionLabel={(option) => option.title}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === "clear") {
            setSearchText(""); // Clear the input value
            setBookSearchResults([]); // Optionally clear the search results
          } else if (reason === "input") {
            handleSearchTextChange({ target: { value: newInputValue } });
          }
        }}
        onChange={(_, value) => value && addBook(value)}
        noOptionsText={
          loading ? (
            <Skeleton variant="rectangular" width="50%" height={40} />
          ) : (
            "No books found"
          )
        }
        renderInput={(params) => (
          <TextField
            {...params}
            sx={{
              width: "50%",
              fontFamily: "Manrope, sans-serif",
            }}
            inputRef={searchInputRef}
            label="Search by title, author, or both"
            variant="outlined"
            fullWidth
            InputProps={{
              ...params.InputProps,
              sx: { borderRadius: "100px", fontFamily: "Manrope, sans-serif" },
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
              startAdornment: (
                <>
                  <IoSearch size={24} color="#6D79C0" />
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            key={option.id || option.title} // Ensure unique key
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              padding: "10px",
              borderBottom: "1px solid #f1f3f5",
            }}
          >
            <Box style={{ flexGrow: 1 }}>
              <Typography
                variant="subtitle1"
                noWrap
                sx={{
                  maxWidth: "500px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {option.title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                sx={{
                  maxWidth: "500px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                By: {option.author}
              </Typography>
            </Box>
            <Box
              component="img"
              src={option.coverImage}
              alt={`${option.title} cover`}
              sx={{
                width: 50,
                height: 50,
                borderRadius: "8px",
                objectFit: "cover",
              }}
            />
          </Box>
        )}
        sx={{
          width: "50%",
        }}
      />
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
          marginLeft: "10px",
          width: "100%",
          justifyContent: "center",
        }}
      >
        <ToggleButton
          value="a"
          style={{
            borderRadius: "100px",
            paddingTop: "0px",
            paddingBottom: "0px",
            fontFamily: "Manrope, sans-serif",
            paddingLeft: "20px",
            paddingRight: "20px",
            textTransform: "none",
            borderColor: searchFilter === "a" ? "#2537A1" : "#BBC1E2",
            backgroundColor: searchFilter !== "a" ? "#FFFFFF" : "#E9EBF6",
          }}
          selected={searchFilter === "a"}
          onClick={() => setSearchFilter("a")}
          size="small"
          disabled={isDisabled}
        >
          Author
        </ToggleButton>
        <ToggleButton
          value="t"
          style={{
            borderRadius: "100px",
            paddingTop: "0px",
            paddingBottom: "0px",
            fontFamily: "Manrope, sans-serif",
            paddingLeft: "20px",
            paddingRight: "20px",
            textTransform: "none",
            borderColor: searchFilter === "t" ? "#2537A1" : "#BBC1E2",
            backgroundColor: searchFilter !== "t" ? "#FFFFFF" : "#E9EBF6",
          }}
          selected={searchFilter === "t"}
          onClick={() => setSearchFilter("t")}
          size="small"
          disabled={isDisabled}
        >
          Title
        </ToggleButton>
        <ToggleButton
          value="b"
          style={{
            borderRadius: "100px",
            fontFamily: "Manrope, sans-serif",
            paddingTop: "0px",
            paddingBottom: "0px",
            paddingLeft: "20px",
            paddingRight: "20px",
            textTransform: "none",
            borderColor: searchFilter === "b" ? "#2537A1" : "#BBC1E2",
            backgroundColor: searchFilter !== "b" ? "#FFFFFF" : "#E9EBF6",
          }}
          selected={searchFilter === "b"}
          onClick={() => setSearchFilter("b")}
          size="small"
          disabled={isDisabled}
        >
          Both
        </ToggleButton>
      </Box>
    </Box>
  );
};

export default BookSearch;
